import type PptxGenJS from 'pptxgenjs';

import {
  FormatType,
  getFormatter,
  NEGATIVE_COLOR,
  OTHER_COLOR,
  POSITIVE_COLOR,
  PRIMARY_COLOR,
  ReplotChartNames,
} from '@revelio/replots';

import clientLogos from '../../../../../../../images/client-logos.svg';
import {
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
} from '../../../../DataProvider/types';
import type { MetricValues } from '../../../../PageConfigProvider/types/metric';
import type { ColumnMetaMap } from '../../../Table/utils/convertSeriesToTableData';
import { getFormat } from '../../../utils/getFormat';
import { buildSheetData, generateChartData } from './ppt-data-processor';
import {
  getActiveBrandConfig,
  getChartOptions,
  PptxChartType,
  pptTheme,
  reportPptTheme,
} from './ppt-theme';

interface PptChartSeries {
  name: string;
  labels: string[];
  values: number[];
}

interface VolumeChartData {
  lineData: PptChartSeries[];
  barData: PptChartSeries[];
}

interface MixedStatsChart {
  name: string;
  labels: (string | number)[];
  values: number[];
  lastValue: number;
  deltaPercentage: number;
}

function isVolumeChartData(data: unknown): data is VolumeChartData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }
  const obj = data as Record<string, unknown>;
  return (
    'lineData' in obj &&
    'barData' in obj &&
    Array.isArray(obj.lineData) &&
    Array.isArray(obj.barData)
  );
}

function isChartSeriesArray(data: unknown): data is PptChartSeries[] {
  return Array.isArray(data);
}

function normalizeLabels(labels: (string | number)[]): string[] {
  return labels.map((label) => String(label));
}

function toPptxHex(color: string): string {
  return typeof color === 'string' ? color.replace('#', '') : '';
}

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Default gauge gradient (blue→red) mirroring GaugeChart's START/END colors. */
const GAUGE_DEFAULT_COLORS = ['55A3FF', 'FF646D'];
const GAUGE_ARC_START_ANGLE = 225;
const GAUGE_ARC_SWEEP = 90;
const GAUGE_ARC_THICKNESS_RATIO = 0.1;
const GAUGE_ARC_SEGMENTS = 24;
const GAUGE_TRACK_LIGHTEN = 0.8;

const hexToRgb = (hex: string): [number, number, number] => {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean.padStart(6, '0').slice(0, 6);
  const num = Number.parseInt(full, 16);
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

const channelToHex = (value: number): string =>
  Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, '0');

const lightenGaugeColor = (hex: string, amount: number): string => {
  const [r, g, b] = hexToRgb(hex);
  return [
    channelToHex(r + (255 - r) * amount),
    channelToHex(g + (255 - g) * amount),
    channelToHex(b + (255 - b) * amount),
  ].join('');
};

/** Linearly interpolate across evenly-spaced color stops, returning a `#`-less hex. */
const interpolateGaugeColor = (colors: string[], t: number): string => {
  const stops = colors.length >= 2 ? colors : GAUGE_DEFAULT_COLORS;
  const scaled = clamp01(t) * (stops.length - 1);
  const lowerIndex = Math.min(stops.length - 2, Math.floor(scaled));
  const frac = scaled - lowerIndex;
  const [r1, g1, b1] = hexToRgb(stops[lowerIndex] ?? '55A3FF');
  const [r2, g2, b2] = hexToRgb(stops[lowerIndex + 1] ?? 'FF646D');
  return [
    channelToHex(r1 + (r2 - r1) * frac),
    channelToHex(g1 + (g2 - g1) * frac),
    channelToHex(b1 + (b2 - b1) * frac),
  ].join('');
};

/**
 * A gauge is only exportable when it has a finite numeric value or a non-empty
 * primary text. This keeps PPT output in sync with the live chart, which
 * renders `ChartError` (and nothing exportable) when it has no data.
 */
export const hasExportableGaugeContent = (gauge: {
  mainText?: string;
  value?: number;
}): boolean => {
  const hasValue =
    typeof gauge.value === 'number' && Number.isFinite(gauge.value);
  const hasMainText = !!gauge.mainText?.trim();
  return hasValue || hasMainText;
};

const renderGaugeContent = (
  slide: PptxGenJS.Slide,
  opts: ReportSlotLayout & {
    mainText?: string;
    subText?: string;
    unit?: string;
    minLabel?: string;
    maxLabel?: string;
    value?: number;
    colors?: string[];
    primaryFontSize: number;
    subFontSize: number;
  }
) => {
  const {
    x,
    y,
    w,
    h,
    mainText,
    subText,
    unit,
    minLabel,
    maxLabel,
    value,
    colors,
    primaryFontSize,
    subFontSize,
  } = opts;

  const hasArc = typeof value === 'number' && Number.isFinite(value);
  const palette =
    colors && colors.length >= 2 ? colors.map(toPptxHex) : GAUGE_DEFAULT_COLORS;
  const hasSub = !!subText?.trim();

  const arcRegionH = hasArc ? Math.min(h * 0.5, 2.2) : 0;

  if (hasArc) {
    const centerX = x + w / 2;
    const topPad = 0.06;
    const cos45 = Math.cos(Math.PI / 4);
    const sin45 = Math.sin(Math.PI / 4);
    const rFromHeight = (arcRegionH - topPad) / (1 - cos45);
    const rFromWidth = (w * 0.82) / (2 * sin45);
    const radius = Math.max(0.3, Math.min(rFromHeight, rFromWidth));
    const boxX = centerX - radius;
    const boxY = y + topPad;
    const boxSize = radius * 2;

    const centerY = boxY + radius;
    const midRadius = radius * (1 - GAUGE_ARC_THICKNESS_RATIO / 2);
    const capRadius = (radius * GAUGE_ARC_THICKNESS_RATIO) / 2;
    const valueAngleDeg =
      GAUGE_ARC_START_ANGLE + clamp01(value) * GAUGE_ARC_SWEEP;
    const segStep = GAUGE_ARC_SWEEP / GAUGE_ARC_SEGMENTS;

    const clampedValue = clamp01(value);

    for (let i = 0; i < GAUGE_ARC_SEGMENTS; i++) {
      const a0 = GAUGE_ARC_START_ANGLE + i * segStep;
      const a1 = a0 + segStep;
      slide.addShape('blockArc', {
        x: boxX,
        y: boxY,
        w: boxSize,
        h: boxSize,
        angleRange: [a0, a1],
        arcThicknessRatio: GAUGE_ARC_THICKNESS_RATIO,
        fill: {
          color: lightenGaugeColor(
            interpolateGaugeColor(palette, (i + 0.5) / GAUGE_ARC_SEGMENTS),
            GAUGE_TRACK_LIGHTEN
          ),
        },
      });
    }

    // Rounded caps on the band ends
    const addRoundedCap = (angleDeg: number, t: number) => {
      const angle = (angleDeg * Math.PI) / 180;
      const capCx = centerX + midRadius * Math.cos(angle);
      const capCy = centerY + midRadius * Math.sin(angle);
      slide.addShape('ellipse', {
        x: capCx - capRadius,
        y: capCy - capRadius,
        w: capRadius * 2,
        h: capRadius * 2,
        fill: {
          color: lightenGaugeColor(
            interpolateGaugeColor(palette, t),
            GAUGE_TRACK_LIGHTEN
          ),
        },
      });
    };
    addRoundedCap(GAUGE_ARC_START_ANGLE, 0);
    addRoundedCap(GAUGE_ARC_START_ANGLE + GAUGE_ARC_SWEEP, 1);

    // Value indicator ("cursor")
    const valueAngle = (valueAngleDeg * Math.PI) / 180;
    const dotRadius = capRadius;
    const dotX = centerX + midRadius * Math.cos(valueAngle);
    const dotY = centerY + midRadius * Math.sin(valueAngle);
    slide.addShape('ellipse', {
      x: dotX - dotRadius,
      y: dotY - dotRadius,
      w: dotRadius * 2,
      h: dotRadius * 2,
      fill: { color: interpolateGaugeColor(palette, clampedValue) },
      line: { color: toPptxHex(pptTheme.colors.white), width: 1.5 },
    });

    const tipOffsetX = radius * sin45;
    const tipY = boxY + radius - radius * cos45;
    if (minLabel?.trim()) {
      slide.addText(minLabel, {
        x: centerX - tipOffsetX - 0.75,
        y: tipY,
        w: 1.5,
        h: 0.3,
        fontSize: subFontSize,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        align: 'center',
        valign: 'top',
      });
    }
    if (maxLabel?.trim()) {
      slide.addText(maxLabel, {
        x: centerX + tipOffsetX - 0.75,
        y: tipY,
        w: 1.5,
        h: 0.3,
        fontSize: subFontSize,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        align: 'center',
        valign: 'top',
      });
    }
  }

  const textTop = y + arcRegionH;
  const textH = Math.max(0.3, y + h - textTop);
  const mainH = hasSub ? textH * 0.6 : textH;
  const subH = hasSub ? textH * 0.4 : 0;
  const fittedPrimary = Math.max(
    10,
    Math.min(primaryFontSize, Math.floor(mainH * 72 * 0.7))
  );

  const displayMain = [mainText, unit].filter((s) => s && s.trim()).join(' ');
  slide.addText(displayMain || '—', {
    x,
    y: textTop,
    w,
    h: mainH,
    fontSize: fittedPrimary,
    bold: true,
    color: pptTheme.colors.brandPrimary,
    fontFace: pptTheme.fonts.face,
    align: 'center',
    valign: 'middle',
  });

  if (hasSub) {
    slide.addText(subText ?? '', {
      x,
      y: textTop + mainH,
      w,
      h: subH,
      fontSize: subFontSize,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'center',
      valign: 'top',
    });
  }
};

function isBreakdownSingleTwoEntity(
  rawData: unknown
): rawData is BreakdownSingleDataSet & {
  data: [
    BreakdownSingleDataSet['data'][number],
    BreakdownSingleDataSet['data'][number],
  ];
} {
  if (typeof rawData !== 'object' || rawData === null) {
    return false;
  }
  const ds = rawData as BreakdownSingleDataSet;
  if (ds.type !== DataType.BREAKDOWN_SINGLE) {
    return false;
  }
  if (!Array.isArray(ds.data) || ds.data.length !== 2) {
    return false;
  }
  return true;
}

function getBreakdownShare(
  entity: { breakdown: SingleDataPoint[] },
  dimension: string
): number {
  const point = entity.breakdown.find((b) => b.dim === dimension);
  return point?.share ?? 0;
}

export const addDivergingBarComboSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  defaultFileName: string | undefined,
  rawData: unknown,
  dataType?: string,
  selectedSegments?: { name: string }[]
) => {
  const slideTitle =
    defaultFileName?.split(' - ').slice(0, -1).join(' - ') || '';

  if (!isBreakdownSingleTwoEntity(rawData)) {
    const { sheetData } = buildSheetData(
      rawData,
      dataType,
      ReplotChartNames.DivergingBarChart
    );
    if (!sheetData || sheetData.length === 0) {
      console.warn('No sheet data for diverging bar combo fallback');
      return;
    }
    const chartData = generateChartData(
      sheetData,
      ReplotChartNames.DivergingBarChart
    );
    const pptChart = {
      type: PptxChartType.bar as PptxGenJS.CHART_NAME,
      opts: {
        barDir: 'bar',
        barGrouping: 'stacked',
        showLegend: false,
        axisLabelFormatCode: '#%',
        catAxisLabelPos: 'low',
        valueBarColors: true,
        chartColors: [
          POSITIVE_COLOR.replace('#', ''),
          NEGATIVE_COLOR.replace('#', ''),
        ],
        catAxisLabelFontSize: 10,
      },
    };
    addSlideContent(
      slide,
      title,
      defaultFileName,
      chartData,
      pptChart,
      ReplotChartNames.DivergingBarChart,
      selectedSegments
    );
    return;
  }

  slide.addText(slideTitle || '', {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const focusEntity = rawData.data[0];
  const baselineEntity = rawData.data[1];

  const dimensionSet = new Set<string>();
  for (const entity of rawData.data) {
    for (const point of entity.breakdown) {
      dimensionSet.add(point.dim);
    }
  }

  const dimensions = Array.from(dimensionSet);
  const rows = dimensions.map((dim) => {
    const baselineShare = getBreakdownShare(baselineEntity, dim);
    const focusShare = getBreakdownShare(focusEntity, dim);
    return {
      dim,
      leftValue: -baselineShare,
      rightValue: focusShare,
    };
  });
  rows.sort((a, b) => {
    const absA = Math.abs(getBreakdownShare(baselineEntity, a.dim));
    const absB = Math.abs(getBreakdownShare(baselineEntity, b.dim));
    if (absA !== absB) {
      return absA - absB;
    }
    return a.dim.localeCompare(b.dim);
  });

  const sortedLabels = rows.map((r) => r.dim);
  const leftValues = rows.map((r) => r.leftValue);
  const rightValues = rows.map((r) => r.rightValue);

  const tornadoSeries = [
    {
      name: baselineEntity.dim,
      labels: sortedLabels,
      values: leftValues,
    },
    {
      name: focusEntity.dim,
      labels: sortedLabels,
      values: rightValues,
    },
  ];

  const differenceValues = sortedLabels.map((dim) => {
    const leftShare = getBreakdownShare(baselineEntity, dim);
    const rightShare = getBreakdownShare(focusEntity, dim);
    return rightShare - leftShare;
  });

  /** Two series (negative vs positive), matching `getDivergingBarConfig` for SingleDataSet difference view. */
  const differenceSeries = [
    {
      name: 'Negative',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d < 0 ? d : 0)),
    },
    {
      name: 'Positive',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d > 0 ? d : 0)),
    },
  ];

  const halfWidth = pptTheme.layout.chartWidth / 2 - 0.15;
  const leftX = pptTheme.layout.slidePaddingX;
  const rightX = pptTheme.layout.slidePaddingX + halfWidth + 0.3;

  slide.addChart(PptxChartType.bar, tornadoSeries, {
    x: leftX,
    y: pptTheme.layout.slidePaddingY,
    w: halfWidth,
    h: pptTheme.layout.chartHeight,
    showTitle: false,
    barDir: 'bar',
    barGrouping: 'stacked',
    showLegend: false,
    chartColors: [toPptxHex(OTHER_COLOR), toPptxHex(PRIMARY_COLOR)],
    catAxisLabelFontSize: 10,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    valAxisLabelFormatCode: '#%',
    catAxisLabelPos: 'low',
  });

  slide.addChart(PptxChartType.bar, differenceSeries, {
    x: rightX,
    y: pptTheme.layout.slidePaddingY,
    w: halfWidth,
    h: pptTheme.layout.chartHeight,
    showTitle: false,
    barDir: 'bar',
    barGrouping: 'stacked',
    showLegend: false,
    chartColors: [toPptxHex(NEGATIVE_COLOR), toPptxHex(POSITIVE_COLOR)],
    catAxisLabelFontSize: 10,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    catAxisHidden: true,
    valAxisLabelFormatCode: '#%',
  });
};

export const addTextSlide = async (
  pptx: PptxGenJS,
  title: string,
  markdownContent: string,
  defaultFileName?: string
) => {
  const { markdownToPptxSlideSegments } = await import(
    './ppt-markdown-converter'
  );
  const slide = pptx.addSlide();
  const slideHeader =
    defaultFileName?.split(' - ').slice(0, -1).join(' - ') || title;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const segments = markdownToPptxSlideSegments(markdownContent);
  let y = 1.0;
  const bodyW = 9;
  const x = 0.5;
  const maxY = 5.2;

  for (const seg of segments) {
    if (seg.kind === 'text' && seg.runs.length > 0) {
      const h = Math.min(4.0, maxY - y);
      if (h < 0.2) {
        break;
      }
      slide.addText(seg.runs, {
        x,
        y,
        w: bodyW,
        h,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        fontSize: 11,
        valign: 'top',
        paraSpaceAfter: 6,
      });
      y += h + 0.05;
    } else if (seg.kind === 'table' && seg.rows.length > 0) {
      const tableRows: PptxGenJS.TableRow[] = seg.rows.map((row) =>
        row.map((cell) => ({ text: cell }))
      );
      const rowCount = tableRows.length;
      const tableH = Math.min(0.35 * rowCount + 0.1, maxY - y);
      if (tableH < 0.15) {
        break;
      }
      slide.addTable(tableRows, {
        x,
        y,
        w: bodyW,
        h: tableH,
        fontSize: 10,
        border: {
          type: 'solid',
          pt: 0.5,
          color: toPptxHex(pptTheme.colors.grid),
        },
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
      });
      y += tableH + 0.15;
    }
  }
};

export const addTableSlide = (
  pptx: PptxGenJS,
  sheetData: Record<string, string | number>[],
  headerTitle: string,
  defaultFileName?: string
) => {
  const slide = pptx.addSlide();
  const slideHeader =
    defaultFileName?.split(' - ').slice(0, -1).join(' - ') || headerTitle;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const firstRow = sheetData?.[0];
  if (!firstRow) {
    return;
  }

  const columns = Object.keys(firstRow);
  if (columns.length === 0) {
    return;
  }

  const tableWidth = 9;
  const columnWidth = tableWidth / columns.length;
  const colW = columns.map(() => columnWidth);

  const brandFill = toPptxHex(pptTheme.colors.brandPrimary);
  const whiteFill = toPptxHex(pptTheme.colors.white);
  const zebraFill = toPptxHex(pptTheme.colors.grid);
  const textColor = toPptxHex(pptTheme.colors.text);
  const whiteText = toPptxHex(pptTheme.colors.white);

  const headerRow: PptxGenJS.TableRow = columns.map((col) => ({
    text: col,
    options: {
      bold: true,
      fill: { color: brandFill },
      color: whiteText,
      fontSize: 9,
    },
  }));

  const dataRows: PptxGenJS.TableRow[] = sheetData.map((row, rowIndex) => {
    const fillColor = rowIndex % 2 === 0 ? whiteFill : zebraFill;
    return columns.map((col) => ({
      text: String(row[col] ?? ''),
      options: {
        fill: { color: fillColor },
      },
    }));
  });

  const allRows: PptxGenJS.TableRow[] = [headerRow, ...dataRows];

  slide.addTable(allRows, {
    x: 0.5,
    y: 1.0,
    w: tableWidth,
    colW,
    fontSize: 8,
    border: {
      type: 'solid',
      pt: 0.5,
      color: toPptxHex(pptTheme.colors.grid),
    },
    autoPage: true,
    autoPageRepeatHeader: true,
    autoPageHeaderRows: 1,
    newSlideStartY: 0.5,
    fontFace: pptTheme.fonts.face,
    color: textColor,
  });
};

export const addMixedStatsSlide = (
  pptx: PptxGenJS,
  charts: MixedStatsChart[],
  headerTitle: string,
  defaultFileName?: string
) => {
  const slide = pptx.addSlide();
  const slideHeader =
    defaultFileName?.split(' - ').slice(0, -1).join(' - ') || headerTitle;
  const headerWidth = pptTheme.layout.header.w;
  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: headerWidth,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const chartWidth = 4.2;
  const chartHeight = 1.4;
  const colGap = 0.3;
  const rowGap = 0.2;
  const startX = 0.5;
  const startY = 0.8;

  const formatLatest = (value: number) => {
    const abs = Math.abs(value ?? 0);
    if (abs <= 2) return `${(value * 100).toFixed(1)}%`;
    return (value ?? 0).toLocaleString();
  };
  const formatDeltaPct = (delta: number) => {
    const sign = delta >= 0 ? '+' : '';
    return `${sign}${delta.toFixed(1)}%`;
  };

  charts.slice(0, 6).forEach((chart, index) => {
    const row = Math.floor(index / 2);
    const col = index % 2;
    const x = startX + col * (chartWidth + colGap);
    const y = startY + row * (chartHeight + rowGap);

    slide.addChart(
      PptxChartType.area,
      [
        {
          name: chart.name,
          labels: normalizeLabels(chart.labels),
          values: chart.values,
        },
      ],
      {
        x,
        y,
        w: chartWidth,
        h: chartHeight,
        showLegend: false,
        catAxisHidden: true,
        catGridLine: { style: 'none' },
        valAxisHidden: true,
        valAxisDisplayUnitLabel: true,
        valGridLine: { style: 'none' },
        showTitle: true,
        title: chart.name,
        chartColors: [pptTheme.colors.areaPrimary.replace('#', '')],
        chartColorsOpacity: 60,
      }
    );

    const lastValue = chart.lastValue ?? 0;
    slide.addText(formatLatest(lastValue), {
      x: x + chartWidth - 1.6,
      y: y + 0.1,
      w: 1.5,
      h: 0.2,
      fontSize: 10,
      bold: true,
      color: pptTheme.colors.text,
      align: 'right',
    });

    slide.addText(formatDeltaPct(chart.deltaPercentage ?? 0), {
      x: x + chartWidth - 1.6,
      y: y + 0.32,
      w: 1.5,
      h: 0.2,
      fontSize: 8,
      color:
        (chart.deltaPercentage ?? 0) >= 0
          ? pptTheme.colors.positive
          : pptTheme.colors.negative,
      align: 'right',
    });
  });
};

export const addSlideContent = (
  slide: PptxGenJS.Slide,
  title: string,
  defaultFileName: string | undefined,
  chartData: unknown,
  pptChart: { type: PptxGenJS.CHART_NAME; opts?: Record<string, unknown> },
  chartType?: ReplotChartNames | string,
  selectedSegments?: { name: string }[]
) => {
  const slideTitle = defaultFileName?.split(' - ').slice(0, -1).join(' - ');

  const headerWidth = pptTheme.layout.header.w;
  slide.addText(slideTitle || '', {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: headerWidth,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    fontFace: pptTheme.fonts.face,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  if (!chartData) return;

  let chartTitle = title;

  if (Array.isArray(selectedSegments) && selectedSegments.length > 0) {
    const segmentNames = selectedSegments.map((s) => s.name).join(', ');
    chartTitle = `${title} - ${segmentNames}`;
  }

  const chartWidth = pptTheme.layout.chartWidth;
  const chartHeight = pptTheme.layout.chartHeight;

  if (
    chartType === ReplotChartNames.VolumeChart &&
    isVolumeChartData(chartData)
  ) {
    if (!chartData.barData[0] || !chartData.barData[1]) {
      console.warn('Invalid volume chart data: missing bar data');
      return;
    }

    const comboTypes: PptxGenJS.IChartMulti[] = [
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[0].name,
            labels: chartData.barData[0].labels,
            values: chartData.barData[0].values,
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.positive)],
          x: pptTheme.layout.slidePaddingX,
          y: pptTheme.layout.slidePaddingY,
          w: chartWidth,
          h: chartHeight,
          title: chartTitle,
          showTitle: true,
          titleColor: toPptxHex(pptTheme.colors.text),
          titleAlign: 'left',
          titleFontFace: pptTheme.fonts.face,
          titleFontSize: pptTheme.fonts.titleSize,
          legendColor: toPptxHex(pptTheme.colors.text),
          legendFontSize: pptTheme.fonts.legendSize,
          catAxisLabelColor: toPptxHex(pptTheme.colors.text),
          catAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
          valAxisLabelColor: toPptxHex(pptTheme.colors.text),
          valAxisLabelFontSize: pptTheme.fonts.axisLabelSize,
          dataLabelColor: toPptxHex(pptTheme.colors.text),
          dataLabelFontSize: pptTheme.fonts.dataLabelSize,
          valGridLine: {
            color: toPptxHex(pptTheme.colors.grid),
            style: 'solid',
          },
          showLegend: true,
          legendPos: 'b',
          barDir: 'col',
          barGrouping: 'stacked',
        },
      },
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[1].name,
            labels: chartData.barData[1].labels,
            values: chartData.barData[1].values.map((v) => -v),
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.negative)],
        },
      },
      {
        type: PptxChartType.area,
        data: chartData.lineData,
        options: {
          chartColors: [toPptxHex(pptTheme.colors.areaSecondary)],
          lineDataSymbol: 'none',
          chartColorsOpacity: 60,
          lineSize: 2,
        },
      },
    ];

    // @ts-expect-error Typings are incorrect. PPTXGenJS errors out when passing a data array in addition to a MultiChart array.
    slide.addChart(comboTypes);
    return;
  }

  if (!isChartSeriesArray(chartData)) {
    console.warn('Invalid chart data format, expected array of chart series');
    return;
  }

  const chartOptions = getChartOptions(chartTitle, pptChart);

  const mutableChartOptions: PptxGenJS.IChartOpts = {
    ...chartOptions,
    chartColors: chartOptions.chartColors
      ? Array.from(chartOptions.chartColors)
      : undefined,
  };

  slide.addChart(pptChart.type, chartData, mutableChartOptions);
};

export type ReportSlotLayout = {
  x: number;
  y: number;
  w: number;
  h: number;
};

const reportChartFontOverrides = {
  titleFontSize: reportPptTheme.fonts.titleSize,
  legendFontSize: reportPptTheme.fonts.legendSize,
  catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
  valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
  dataLabelFontSize: reportPptTheme.fonts.dataLabelSize,
} as const;

const lineChartReportOverrides = {
  catAxisLabelFrequency: '12',
  catLabelFormatCode: 'yyyy',
} as const;

const reportChartTitleWithSegments = (
  title: string,
  selectedSegments?: { name: string }[]
): string => {
  const base = title.trim();
  if (Array.isArray(selectedSegments) && selectedSegments.length > 0) {
    const segmentNames = selectedSegments.map((s) => s.name).join(', ');
    return base ? `${base} - ${segmentNames}` : segmentNames;
  }
  return base;
};

export const addReportChartToSlide = (
  slide: PptxGenJS.Slide,
  chartData: unknown,
  pptChart: { type: PptxGenJS.CHART_NAME; opts?: Record<string, unknown> },
  opts: ReportSlotLayout & {
    title: string;
    chartType?: ReplotChartNames | string;
    selectedSegments?: { name: string }[];
  }
) => {
  const { x, y, w, h, title, chartType, selectedSegments } = opts;

  if (!chartData) return;

  const titleWithSegments = reportChartTitleWithSegments(
    title,
    selectedSegments
  );
  const hasTitle = titleWithSegments.trim().length > 0;

  if (
    chartType === ReplotChartNames.VolumeChart &&
    isVolumeChartData(chartData)
  ) {
    if (!chartData.barData[0] || !chartData.barData[1]) {
      console.warn('Invalid volume chart data: missing bar data');
      return;
    }

    const comboTypes: PptxGenJS.IChartMulti[] = [
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[0].name,
            labels: chartData.barData[0].labels,
            values: chartData.barData[0].values,
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.positive)],
          x,
          y,
          w,
          h,
          title: hasTitle ? titleWithSegments : '',
          showTitle: hasTitle,
          titleColor: toPptxHex(pptTheme.colors.text),
          titleAlign: 'left',
          titleFontFace: pptTheme.fonts.face,
          ...reportChartFontOverrides,
          legendColor: toPptxHex(pptTheme.colors.text),
          catAxisLabelColor: toPptxHex(pptTheme.colors.text),
          valAxisLabelColor: toPptxHex(pptTheme.colors.text),
          dataLabelColor: toPptxHex(pptTheme.colors.text),
          valGridLine: {
            color: toPptxHex(pptTheme.colors.grid),
            style: 'solid',
          },
          showLegend: true,
          legendPos: 'b',
          barDir: 'col',
          barGrouping: 'stacked',
        },
      },
      {
        type: PptxChartType.bar,
        data: [
          {
            name: chartData.barData[1].name,
            labels: chartData.barData[1].labels,
            values: chartData.barData[1].values.map((v) => -v),
          },
        ],
        options: {
          chartColors: [toPptxHex(pptTheme.colors.negative)],
        },
      },
      {
        type: PptxChartType.area,
        data: chartData.lineData,
        options: {
          chartColors: [toPptxHex(pptTheme.colors.areaSecondary)],
          lineDataSymbol: 'none',
          chartColorsOpacity: 60,
          lineSize: 2,
        },
      },
    ];
    // @ts-expect-error Typings are incorrect. PPTXGenJS errors out when passing a data array in addition to a MultiChart array.
    slide.addChart(comboTypes);
    return;
  }

  if (!isChartSeriesArray(chartData)) {
    console.warn('Invalid chart data format, expected array of chart series');
    return;
  }

  const baseChartOptions = getChartOptions(titleWithSegments || ' ', pptChart);
  const lineOverrides =
    chartType === ReplotChartNames.LineChart ? lineChartReportOverrides : {};
  const mutableChartOptions: PptxGenJS.IChartOpts = {
    ...baseChartOptions,
    ...reportChartFontOverrides,
    ...lineOverrides,
    x,
    y,
    w,
    h,
    showTitle: hasTitle,
    title: hasTitle ? titleWithSegments : '',
    chartColors: baseChartOptions.chartColors
      ? Array.from(baseChartOptions.chartColors)
      : undefined,
  };

  slide.addChart(pptChart.type, chartData, mutableChartOptions);
};

export const addReportDivergingBarComboToSlide = (
  slide: PptxGenJS.Slide,
  rawData: unknown,
  opts: ReportSlotLayout & {
    title: string;
    dataType?: string;
    selectedSegments?: { name: string }[];
  }
) => {
  const { x, y, w, h, title, dataType, selectedSegments } = opts;

  if (!isBreakdownSingleTwoEntity(rawData)) {
    const { sheetData } = buildSheetData(
      rawData,
      dataType,
      ReplotChartNames.DivergingBarChart
    );
    if (!sheetData || sheetData.length === 0) {
      console.warn('No sheet data for diverging bar combo fallback');
      return;
    }
    const chartData = generateChartData(
      sheetData,
      ReplotChartNames.DivergingBarChart
    );
    const pptChart = {
      type: PptxChartType.bar as PptxGenJS.CHART_NAME,
      opts: {
        barDir: 'bar',
        barGrouping: 'stacked',
        showLegend: false,
        axisLabelFormatCode: '#%',
        catAxisLabelPos: 'low',
        valueBarColors: true,
        chartColors: [
          POSITIVE_COLOR.replace('#', ''),
          NEGATIVE_COLOR.replace('#', ''),
        ],
        catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
      },
    };
    addReportChartToSlide(slide, chartData, pptChart, {
      x,
      y,
      w,
      h,
      title,
      chartType: ReplotChartNames.DivergingBarChart,
      selectedSegments,
    });
    return;
  }

  const titleTrim = title.trim();
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  const focusEntity = rawData.data[0];
  const baselineEntity = rawData.data[1];

  const dimensionSet = new Set<string>();
  for (const entity of rawData.data) {
    for (const point of entity.breakdown) {
      dimensionSet.add(point.dim);
    }
  }

  const dimensions = Array.from(dimensionSet);
  const rows = dimensions.map((dim) => {
    const baselineShare = getBreakdownShare(baselineEntity, dim);
    const focusShare = getBreakdownShare(focusEntity, dim);
    return {
      dim,
      leftValue: -baselineShare,
      rightValue: focusShare,
    };
  });
  rows.sort((a, b) => {
    const absA = Math.abs(getBreakdownShare(baselineEntity, a.dim));
    const absB = Math.abs(getBreakdownShare(baselineEntity, b.dim));
    if (absA !== absB) {
      return absA - absB;
    }
    return a.dim.localeCompare(b.dim);
  });

  const sortedLabels = rows.map((r) => r.dim);
  const leftValues = rows.map((r) => r.leftValue);
  const rightValues = rows.map((r) => r.rightValue);

  const tornadoSeries = [
    {
      name: baselineEntity.dim,
      labels: sortedLabels,
      values: leftValues,
    },
    {
      name: focusEntity.dim,
      labels: sortedLabels,
      values: rightValues,
    },
  ];

  const differenceValues = sortedLabels.map((dim) => {
    const leftShare = getBreakdownShare(baselineEntity, dim);
    const rightShare = getBreakdownShare(focusEntity, dim);
    return rightShare - leftShare;
  });

  const differenceSeries = [
    {
      name: 'Negative',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d < 0 ? d : 0)),
    },
    {
      name: 'Positive',
      labels: sortedLabels,
      values: differenceValues.map((d) => (d > 0 ? d : 0)),
    },
  ];

  const gap = 0.15;
  const halfW = (w - gap) / 2;
  const chartY = y + headerH + 0.05;
  const chartH = Math.max(0.5, h - headerH - 0.05);
  const leftX = x;
  const rightX = x + halfW + gap;

  slide.addChart(PptxChartType.bar, tornadoSeries, {
    x: leftX,
    y: chartY,
    w: halfW,
    h: chartH,
    showTitle: false,
    barDir: 'bar',
    barGrouping: 'stacked',
    showLegend: false,
    chartColors: [toPptxHex(OTHER_COLOR), toPptxHex(PRIMARY_COLOR)],
    catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    valAxisLabelFormatCode: '#%',
    catAxisLabelPos: 'low',
  });

  slide.addChart(PptxChartType.bar, differenceSeries, {
    x: rightX,
    y: chartY,
    w: halfW,
    h: chartH,
    showTitle: false,
    barDir: 'bar',
    barGrouping: 'stacked',
    showLegend: false,
    chartColors: [toPptxHex(NEGATIVE_COLOR), toPptxHex(POSITIVE_COLOR)],
    catAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    catAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelColor: toPptxHex(pptTheme.colors.text),
    valAxisLabelFontSize: reportPptTheme.fonts.axisLabelSize,
    valGridLine: { color: toPptxHex(pptTheme.colors.grid), style: 'solid' },
    catAxisHidden: true,
    valAxisLabelFormatCode: '#%',
  });
};

export const addGaugeSlide = (
  slide: PptxGenJS.Slide,
  title: string,
  defaultFileName: string | undefined,
  gauge: {
    mainText?: string;
    subText?: string;
    unit?: string;
    value?: number;
    colors?: string[];
    minLabel?: string;
    maxLabel?: string;
  }
) => {
  const slideHeader =
    defaultFileName?.split(' - ').slice(0, -1).join(' - ') || title;

  slide.addText(slideHeader, {
    x: pptTheme.layout.header.x,
    y: pptTheme.layout.header.y,
    w: pptTheme.layout.header.w,
    h: pptTheme.layout.header.h,
    fontSize: pptTheme.fonts.headerTitleSize,
    bold: true,
    color: pptTheme.colors.text,
    align: 'left',
  });

  const contentX = pptTheme.layout.slidePaddingX;
  const contentY = pptTheme.layout.slidePaddingY;
  const contentW = pptTheme.layout.chartWidth;
  const contentH = pptTheme.layout.chartHeight;

  // The slide header is the report/page name (the chart title is sliced off the
  // file name), so render the gauge's own title above the value the way other
  // chart types surface it inside their plot.
  const chartTitleTrim = title?.trim() ?? '';
  const showChartTitle =
    chartTitleTrim.length > 0 && chartTitleTrim !== slideHeader.trim();
  const chartTitleH = showChartTitle ? 0.5 : 0;
  if (showChartTitle) {
    slide.addText(chartTitleTrim, {
      x: contentX,
      y: contentY,
      w: contentW,
      h: chartTitleH,
      fontSize: pptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'center',
      valign: 'middle',
    });
  }

  renderGaugeContent(slide, {
    x: contentX,
    y: contentY + chartTitleH,
    w: contentW,
    h: Math.max(0.4, contentH - chartTitleH),
    mainText: gauge.mainText,
    subText: gauge.subText,
    unit: gauge.unit,
    minLabel: gauge.minLabel,
    maxLabel: gauge.maxLabel,
    value: gauge.value,
    colors: gauge.colors,
    primaryFontSize: reportPptTheme.fonts.gaugePrimaryTextSize,
    subFontSize: reportPptTheme.fonts.gaugeSubTextSize,
  });
};

export const addReportGaugeToSlide = (
  slide: PptxGenJS.Slide,
  opts: ReportSlotLayout & {
    title?: string;
    mainText?: string;
    subText?: string;
    unit?: string;
    value?: number;
    colors?: string[];
    minLabel?: string;
    maxLabel?: string;
  }
) => {
  const {
    x,
    y,
    w,
    h,
    title,
    mainText,
    subText,
    unit,
    value,
    colors,
    minLabel,
    maxLabel,
  } = opts;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  renderGaugeContent(slide, {
    x,
    y: y + headerH,
    w,
    h: Math.max(0.4, h - headerH),
    mainText,
    subText,
    unit,
    minLabel,
    maxLabel,
    value,
    colors,
    primaryFontSize: reportPptTheme.fonts.gaugePrimaryTextSize,
    subFontSize: reportPptTheme.fonts.gaugeSubTextSize,
  });
};

export const addReportTextToSlide = async (
  slide: PptxGenJS.Slide,
  markdownContent: string,
  opts: ReportSlotLayout & { title?: string }
) => {
  const { markdownToPptxSlideSegments } = await import(
    './ppt-markdown-converter'
  );

  const { x, y, w, h, title } = opts;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  const segments = markdownToPptxSlideSegments(markdownContent);
  let cursorY = y + headerH;
  const maxY = y + h;

  for (const seg of segments) {
    if (seg.kind === 'text' && seg.runs.length > 0) {
      const available = maxY - cursorY;
      if (available < 0.2) break;
      const segH = Math.min(4.0, available);
      slide.addText(seg.runs, {
        x,
        y: cursorY,
        w,
        h: segH,
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
        fontSize: 10,
        valign: 'top',
        paraSpaceAfter: 4,
      });
      cursorY += segH + 0.05;
    } else if (seg.kind === 'table' && seg.rows.length > 0) {
      const tableRows: PptxGenJS.TableRow[] = seg.rows.map((row) =>
        row.map((cell) => ({ text: cell }))
      );
      const rowCount = tableRows.length;
      const tableH = Math.min(0.3 * rowCount + 0.1, maxY - cursorY);
      if (tableH < 0.15) break;
      slide.addTable(tableRows, {
        x,
        y: cursorY,
        w,
        h: tableH,
        fontSize: 9,
        border: {
          type: 'solid',
          pt: 0.5,
          color: toPptxHex(pptTheme.colors.grid),
        },
        color: pptTheme.colors.text,
        fontFace: pptTheme.fonts.face,
      });
      cursorY += tableH + 0.1;
    }
  }
};

const resolveReportTableColumnFormat = ({
  columnKey,
  columnMetaMap,
  plotFormat,
}: {
  columnKey: string;
  columnMetaMap: ColumnMetaMap | undefined;
  plotFormat: FormatType | undefined;
}): FormatType | string => {
  const columnMeta = columnMetaMap?.[columnKey];
  if (
    columnMeta?.format !== undefined &&
    columnMeta.format !== null &&
    columnMeta.format !== ''
  ) {
    return columnMeta.format;
  }
  const metricForColumn = columnMeta?.metric;
  if (metricForColumn !== undefined) {
    return getFormat({
      metric: metricForColumn as MetricValues,
      displayAs: 'value',
    });
  }
  if (
    columnMetaMap === undefined &&
    (columnKey === 'VALUE' || columnKey === 'SHARE') &&
    plotFormat !== undefined
  ) {
    return plotFormat;
  }
  return FormatType.SI;
};

const formatReportTableCell = ({
  columnKey,
  value,
  columnMetaMap,
  plotFormat,
  currencyCode,
}: {
  columnKey: string;
  value: unknown;
  columnMetaMap: ColumnMetaMap | undefined;
  plotFormat: FormatType | undefined;
  currencyCode?: string;
}): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const format = resolveReportTableColumnFormat({
    columnKey,
    columnMetaMap,
    plotFormat,
  });
  const formatter = getFormatter(format, { currencyCode });
  const numValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isNaN(numValue) && value !== '') {
    return formatter(numValue);
  }
  return String(value);
};

export const addReportTableToSlide = (
  slide: PptxGenJS.Slide,
  sheetData: Record<string, string | number>[],
  opts: ReportSlotLayout & {
    title?: string;
    columnMetaMap?: ColumnMetaMap;
    tablePlotFormat?: FormatType;
    currencyCode?: string;
  }
) => {
  const { x, y, w, h, title, columnMetaMap, tablePlotFormat, currencyCode } =
    opts;

  const firstRow = sheetData?.[0];
  if (!firstRow) return;
  const columns = Object.keys(firstRow);
  if (columns.length === 0) return;

  const titleTrim = title?.trim() ?? '';
  const headerH = titleTrim ? 0.3 : 0;
  if (titleTrim) {
    slide.addText(titleTrim, {
      x,
      y,
      w,
      h: headerH,
      fontSize: reportPptTheme.fonts.titleSize,
      bold: true,
      color: pptTheme.colors.text,
      fontFace: pptTheme.fonts.face,
      align: 'left',
    });
  }

  const columnWidth = w / columns.length;
  const colW = columns.map(() => columnWidth);

  const brandFill = toPptxHex(pptTheme.colors.brandPrimary);
  const whiteFill = toPptxHex(pptTheme.colors.white);
  const zebraFill = toPptxHex(pptTheme.colors.grid);
  const textColor = toPptxHex(pptTheme.colors.text);
  const whiteText = toPptxHex(pptTheme.colors.white);

  const headerRow: PptxGenJS.TableRow = columns.map((col) => ({
    text: col,
    options: {
      bold: true,
      fill: { color: brandFill },
      color: whiteText,
      fontSize: 8,
    },
  }));

  const dataRows: PptxGenJS.TableRow[] = sheetData.map((row, rowIndex) => {
    const fillColor = rowIndex % 2 === 0 ? whiteFill : zebraFill;
    return columns.map((col) => ({
      text: formatReportTableCell({
        columnKey: col,
        value: row[col],
        columnMetaMap,
        plotFormat: tablePlotFormat,
        currencyCode,
      }),
      options: { fill: { color: fillColor } },
    }));
  });

  slide.addTable([headerRow, ...dataRows], {
    x,
    y: y + headerH,
    w,
    h: Math.max(0.1, h - headerH),
    colW,
    fontSize: 7,
    border: {
      type: 'solid',
      pt: 0.5,
      color: toPptxHex(pptTheme.colors.grid),
    },
    fontFace: pptTheme.fonts.face,
    color: textColor,
  });
};

const FOOTER_TEXT = {
  aboutTitle: 'About Revelio Labs data',
  aboutBody:
    'Revelio Labs provides workforce intelligence. We absorb and standardize hundreds of millions of public employment records to create the world\u2019s first universal HR database, allowing us to see current workforce composition of any company. We use the most advanced techniques in data science and statistical modeling to provide clean, reliable workforce data, which we additionally enrich with our proprietary models for greater insight into workforce trends. With our salary prediction model, we are able to estimate salaries for more than 1.4 billion unique positions worldwide, using individual-level information such as job title, seniority level, company, and location.',
  contactTitle: 'Contact',
  contactBody:
    'Thank you for your interest in our data.\n\nBy ingesting the universe of publicly available online profiles, job postings, employee sentiment reviews, and layoff notices, Revelio Labs has created a comprehensive source of workforce data that can be used to analyze companies, occupations, skills, and activities. In addition to attrition, our team of labor economists and data scientists can create customized reports on industry outlooks, talent intelligence, DEI, and more.\n\nFor additional information on the custom insights we can offer, please reach out to info@reveliolabs.com',
  getInTouchTitle: 'Get in touch',
  getInTouchBody:
    'Reach out for additional information on the custom insights we can offer.',
  emailTitle: 'Email us',
  emailBody: 'info@reveliolabs.com',
  visitTitle: 'Visit us',
  visitBody: '860 Broadway, 5th floor, New York, NY 10003',
} as const;

const textColor = toPptxHex(pptTheme.colors.brandPrimary);

type ClosingSlideConfig = NonNullable<
  ReturnType<typeof getActiveBrandConfig>['closingSlide']
>;

/** Natural size of the lockup asset (1922 x 737.33) → height/width ratio. */
const CLOSING_LOCKUP_ASPECT = 737.33 / 1922;

/**
 * Branded closing slide: solid background + centred logo/tagline lockup + a
 * dynamic-year copyright footer. Replaces the default Revelio info slide for
 * brands that set `closingSlide`. Works for both landscape and portrait
 * slide sizes (the caller passes the slide dimensions).
 */
const addBrandedClosingSlide = (
  pptx: PptxGenJS,
  config: ClosingSlideConfig,
  slide: { widthInches: number; heightInches: number }
) => {
  const s = pptx.addSlide();
  s.background = { color: config.backgroundColor.replace('#', '') };

  const logoW = slide.widthInches * 0.3;
  const logoH = logoW * CLOSING_LOCKUP_ASPECT;
  s.addImage({
    path: config.logoImage,
    x: (slide.widthInches - logoW) / 2,
    y: (slide.heightInches - logoH) / 2,
    w: logoW,
    h: logoH,
    sizing: { type: 'contain', w: logoW, h: logoH },
  });

  const year = new Date().getFullYear();
  s.addText(`© ${year}, ${config.copyrightHolder}. All rights reserved.`, {
    x: 0.4,
    y: slide.heightInches - 0.5,
    w: slide.widthInches - 0.8,
    h: 0.3,
    fontFace: pptTheme.fonts.face,
    fontSize: 9,
    color: '595959',
    align: 'left',
    valign: 'middle',
  });
};

export const addReportFooterSlide = (pptx: PptxGenJS) => {
  const brand = getActiveBrandConfig();
  if (brand.closingSlide) {
    const { widthInches, heightInches } = reportPptTheme.slide;
    addBrandedClosingSlide(pptx, brand.closingSlide, {
      widthInches,
      heightInches,
    });
    return;
  }

  const { widthInches, marginInches } = reportPptTheme.slide;
  const slide = pptx.addSlide();

  const contentW = widthInches - 2 * marginInches;
  const x = marginInches;
  let cursorY = marginInches;

  const columnGap = 0.12;
  const aboutImageRowH = 2.05;
  const aboutColW = (contentW - columnGap) * (3 / 4);
  const imageColW = (contentW - columnGap) * (1 / 4);

  const CLIENT_LOGOS_VIEWBOX_W = 232;
  const CLIENT_LOGOS_VIEWBOX_H = 257;
  const logoAspect = CLIENT_LOGOS_VIEWBOX_H / CLIENT_LOGOS_VIEWBOX_W;
  const logoMaxW = Math.min(1.35, imageColW - 0.06);
  let logoW = logoMaxW;
  let logoH = logoW * logoAspect;
  const maxLogoH = aboutImageRowH - 0.08;
  if (logoH > maxLogoH) {
    logoH = maxLogoH;
    logoW = logoH / logoAspect;
  }
  const logoX = x + aboutColW + columnGap + (imageColW - logoW) / 2;
  const logoY = cursorY + (aboutImageRowH - logoH) / 2;

  slide.addText(
    [
      {
        text: FOOTER_TEXT.aboutTitle,
        options: { fontSize: 12, bold: true, breakLine: true },
      },
      { text: FOOTER_TEXT.aboutBody, options: { fontSize: 8 } },
    ],
    {
      x,
      y: cursorY,
      w: aboutColW,
      h: aboutImageRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );

  slide.addImage({
    path: clientLogos,
    x: logoX,
    y: logoY,
    w: logoW,
    h: logoH,
    sizing: { type: 'contain', w: logoW, h: logoH },
  });

  cursorY += aboutImageRowH + 0.22;

  const contactRowH = 2.35;
  const contactColW = (contentW - columnGap) * (3 / 5);
  const getInTouchColW = (contentW - columnGap) * (2 / 5);

  slide.addText(
    [
      {
        text: FOOTER_TEXT.contactTitle,
        options: { fontSize: 12, bold: true, breakLine: true },
      },
      { text: FOOTER_TEXT.contactBody, options: { fontSize: 8 } },
    ],
    {
      x,
      y: cursorY,
      w: contactColW,
      h: contactRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );

  slide.addText(
    [
      {
        text: FOOTER_TEXT.getInTouchTitle,
        options: { fontSize: 10, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.getInTouchBody,
        options: { fontSize: 8, breakLine: true },
      },
      { text: '', options: { fontSize: 5, breakLine: true } },
      {
        text: `${FOOTER_TEXT.emailTitle}: `,
        options: { fontSize: 9, bold: true },
      },
      {
        text: FOOTER_TEXT.emailBody,
        options: { fontSize: 9, breakLine: true },
      },
      {
        text: `${FOOTER_TEXT.visitTitle}: `,
        options: { fontSize: 9, bold: true },
      },
      { text: FOOTER_TEXT.visitBody, options: { fontSize: 9 } },
    ],
    {
      x: x + contactColW + columnGap,
      y: cursorY,
      w: getInTouchColW,
      h: contactRowH,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 3,
    }
  );
};

export const addFooterSlide = (pptx: PptxGenJS) => {
  const brand = getActiveBrandConfig();
  if (brand.closingSlide) {
    addBrandedClosingSlide(pptx, brand.closingSlide, {
      widthInches: 10,
      heightInches: 5.625,
    });
    return;
  }

  const slide = pptx.addSlide();

  slide.addText(
    [
      {
        text: FOOTER_TEXT.aboutTitle,
        options: { fontSize: 14, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.aboutBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 0.5,
      y: 0.4,
      w: 6.2,
      h: 2.2,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 6,
    }
  );

  slide.addImage({
    path: clientLogos,
    x: 7.2,
    y: 0.4,
    w: 2.0,
    h: 2.0,
    sizing: { type: 'contain', w: 2.0, h: 2.0 },
  });

  slide.addText(
    [
      {
        text: FOOTER_TEXT.contactTitle,
        options: { fontSize: 14, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.contactBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 0.5,
      y: 2.8,
      w: 5.5,
      h: 2.4,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 6,
    }
  );

  slide.addText(
    [
      {
        text: FOOTER_TEXT.getInTouchTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.getInTouchBody,
        options: { fontSize: 9, breakLine: true },
      },
      { text: '', options: { fontSize: 6, breakLine: true } },
      {
        text: FOOTER_TEXT.emailTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.emailBody,
        options: { fontSize: 9, breakLine: true },
      },
      { text: '', options: { fontSize: 6, breakLine: true } },
      {
        text: FOOTER_TEXT.visitTitle,
        options: { fontSize: 11, bold: true, breakLine: true },
      },
      {
        text: FOOTER_TEXT.visitBody,
        options: { fontSize: 9 },
      },
    ],
    {
      x: 6.5,
      y: 2.8,
      w: 3.0,
      h: 2.4,
      color: textColor,
      fontFace: pptTheme.fonts.face,
      valign: 'top',
      paraSpaceAfter: 4,
    }
  );
};
